import { createContext, useContext } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { BRAND_DEFAULTS, DEFAULT_SPONSORS } from '../data';

export const EASE = [0.16, 1, 0.3, 1];

export const AppContext = createContext({
  openModal: () => {},
  closeModal: () => {},
  scrollToId: () => {},
});

export const useApp = () => useContext(AppContext);

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/** Public site config managed from the admin panel (logos, sponsors, agenda). */
export function useSite() {
  const { data, isLoading } = useQuery({
    queryKey: ['site'],
    queryFn: async () => {
      const r = await fetch(`${API_BASE}/site`);
      if (!r.ok) throw new Error('site');
      return r.json();
    },
    retry: 1,
  });
  const settings = { ...BRAND_DEFAULTS, ...((data && data.settings) || {}) };
  Object.keys(BRAND_DEFAULTS).forEach((k) => {
    if (!settings[k]) settings[k] = BRAND_DEFAULTS[k];
  });
  const sponsors = data && Array.isArray(data.sponsors) ? data.sponsors : DEFAULT_SPONSORS;
  const agenda = (data && Array.isArray(data.agenda) && data.agenda) || [];
  return { settings, sponsors, agenda, loading: isLoading };
}

export function Reveal({ children, delay = 0, y = 32, className = '' }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function SectionHead({ eyebrow, title, className = '' }) {
  return (
    <div className={`mb-12 md:mb-16 ${className}`}>
      <Reveal>
        <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] uppercase text-lime">
          <span className="h-px w-8 bg-lime inline-block" />
          {eyebrow}
        </div>
      </Reveal>
      <Reveal delay={0.08}>
        <h2 className="mt-5 font-display font-bold tracking-tight leading-[1.04] text-3xl sm:text-4xl lg:text-[3.4rem]">
          {title}
        </h2>
      </Reveal>
    </div>
  );
}

export function Reticle({ className = 'w-9 h-9' }) {
  return (
    <svg viewBox="0 0 64 64" fill="none" className={className} aria-hidden="true">
      <circle cx="32" cy="32" r="20" stroke="#99CC00" strokeWidth="3.5" />
      <circle cx="32" cy="32" r="5.5" fill="#99CC00" />
      <path d="M32 4v8M32 52v8M4 32h8M52 32h8" stroke="#99CC00" strokeWidth="3.5" strokeLinecap="round" />
    </svg>
  );
}
